'use client';

import React, { useEffect } from 'react';
import {
  AmplitudeEvents,
  AmplitudeProperties,
} from 'whoop-analytics/lib/types';
import { UUID_COOKIE_NAME } from 'whoop-analytics/lib/sources/experiment';
import { whoopAnalyticsScrollTracker } from 'whoop-analytics/lib/sources/whoop-analytics';
import { useShallow } from 'zustand/react/shallow';
import type { JoinFlowContent } from 'services/generated/growth-content-service/models/JoinFlowContent';
import type { CheckoutPromo } from 'services/generated/membership-service-v2/models/CheckoutPromo';
import { setCookie } from 'whoop-analytics/utils';
import { createClientAuth } from '@whoop/auth-client';
import type { CatalogResponse } from 'services/generated/commerce-service/models/CatalogResponse';
import type { ParsedCartDTO } from 'ui/types/cartTypes';
import { useStripe } from '@stripe/react-stripe-js';
import { getOneTouchDigitalWalletOptionsV2 } from 'ui/utils/paymentHelpers';
import { useAnalytics } from '@/hooks/analytics';
import { useSiteStore } from '@/store/SiteStoreProvider';
import type {
  IInitialSiteData,
  PromoType,
} from '@/lib/utils/initialDataFetcher';
import {
  JF_BAND_SELECTOR,
  JF_BAND_SELECTOR_HEADER,
  JF_EXPRESS_CHECKOUT,
} from '@/lib/constants/experiments';
import type { SiteWidePromoContent } from '@/types/siteWidePromoTypes';
import LandingPageContent from './LandingPageContent/LandingPageContent';
import { getHasFamilyMemberships } from '@/lib/utils/familyHelpers';
import { env } from 'env';
import { usePathname } from 'next/navigation';
import { getStripeAccount } from '@/stripe/stripeUtils';

interface LandingPageProps {
  featureFlags?: Record<string, boolean>;
  isMobile?: boolean;
  initialData: IInitialSiteData;
  ecommCatalog: CatalogResponse;
  promoContent?: JoinFlowContent;
  promo?: CheckoutPromo;
  sitewidePromoContent?: SiteWidePromoContent;
  language?: string;
  experimentUUID?: string;
  promoType: PromoType;
  skipMemSelection?: string;
  skipMemFamilySize?: string;
  discountedCart?: ParsedCartDTO;
  experiments: Record<string, string>;
}

export default function LandingPage({
  featureFlags,
  isMobile,
  initialData,
  ecommCatalog,
  promo,
  promoContent,
  promoType,
  sitewidePromoContent,
  experimentUUID,
  skipMemSelection,
  skipMemFamilySize,
  discountedCart,
  experiments,
}: LandingPageProps): JSX.Element {
  const auth = createClientAuth({
    env: env.NEXT_PUBLIC_ENV,
    segmentKey: env.NEXT_PUBLIC_SEGMENT_API_KEY,
  });

  const {
    initializeFeatureFlags,
    initializePromo,
    setEcommCatalog,
    setReturnShippingCost,
    setIsRafGiftFlow,
    setPromoType,
    setEcommCart,
    setSkipMemSelectFlow,
    country,
    currency,
    setStripePaymentRequest,
    cart,
    setStripeAccount,
    setExperimentVariant,
  } = useSiteStore(
    useShallow((state) => ({
      initializeFeatureFlags: state.initializeFeatureFlags,
      initializePromo: state.initializePromo,
      setEcommCatalog: state.setEcommCatalog,
      setReturnShippingCost: state.setReturnShippingCost,
      setIsRafGiftFlow: state.setIsRafGiftFlow,
      setPromoType: state.setPromoType,
      currency: state.currency,
      setEcommCart: state.setEcommCart,
      setSkipMemSelectFlow: state.setSkipMemSelectFlow,
      country: state.country,
      setStripePaymentRequest: state.setStripePaymentRequest,
      cart: state.cart,
      setStripeAccount: state.setStripeAccount,
      setExperimentVariant: state.setExperimentVariant,
    })),
  );
  const pathname = usePathname();
  const stripe = useStripe();
  const {
    initialize,
    isInitialized,
    trackPageView,
    trackAmplitudeEvent,
    setAmplitudeUserProperty,
  } = useAnalytics();

  const hasFamilyMemberships = getHasFamilyMemberships(
    ecommCatalog.memberships,
  );

  useEffect(() => {
    if (isInitialized) {
      const label = hasFamilyMemberships
        ? AmplitudeProperties.FamilyMembershipVisible
        : undefined;
      trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
        skip_mem_select: skipMemSelection,
        family_size: skipMemFamilySize,
        label,
      });
    }

    // Don't have trackAmplitudeEvent as a dependency because otherwise this useEffect runs more than it need to
  }, [isInitialized]);

  const initializeStripePaymentOptions = async () => {
    if (!stripe) return;

    const options = getOneTouchDigitalWalletOptionsV2({
      cart,
      currency,
      stripeCountry: '',
    });

    const stripeAccount = getStripeAccount(country);

    const request = stripe.paymentRequest({
      ...options,
      // This is for the region that our stripe account is setup in. Not the users country.
      country: stripeAccount === 'EU' ? 'IE' : 'US',
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true,
    });

    const paymentRequestOptions = await request.canMakePayment();
    if (!paymentRequestOptions) {
      setAmplitudeUserProperty('express-checkout-available', 'false');
      setAmplitudeUserProperty(JF_EXPRESS_CHECKOUT, 'none');
      setStripePaymentRequest();
      return;
    }

    setStripePaymentRequest({
      request,
      paymentOptionsAvailable: paymentRequestOptions,
    });
  };

  function init(): void {
    // If a token exists, it will be validated when the user gets to the gift page in middleware
    // This is to ensure we don't show gift copy to users on the landing page
    const isRafGift = Boolean(auth.getToken()) && pathname.includes('/gift/');
    setIsRafGiftFlow(isRafGift);
    setEcommCatalog(ecommCatalog);
    if (discountedCart) {
      setEcommCart(discountedCart);
    }

    //Feature Flags
    initializeFeatureFlags(featureFlags ?? {});

    // Promo type and return shipping cost
    setReturnShippingCost(initialData.returnShippingCost);
    setPromoType(promoType);

    setStripeAccount(getStripeAccount(country));

    // Promo Content
    let initialPromoContent = promoContent;

    // Set experiment variants in store and amplitude User properties
    for (let exp in experiments) {
      if (exp === JF_BAND_SELECTOR_HEADER) continue;
      setExperimentVariant(exp, experiments[exp]);
      setAmplitudeUserProperty(exp, experiments[exp]);
    }

    /// If the band selector experiment is control, we want to set the variant to control
    // If the band selector experiment is not control, we want to set the variant to the value of the experiment
    if (experiments?.[JF_BAND_SELECTOR] === 'control') {
      setExperimentVariant(JF_BAND_SELECTOR_HEADER, 'control');
      setAmplitudeUserProperty(JF_BAND_SELECTOR_HEADER, 'control');
    } else {
      setExperimentVariant(
        JF_BAND_SELECTOR_HEADER,
        experiments?.[JF_BAND_SELECTOR_HEADER],
      );
      setAmplitudeUserProperty(
        JF_BAND_SELECTOR_HEADER,
        experiments?.[JF_BAND_SELECTOR_HEADER],
      );
    }

    initializePromo(
      initialPromoContent,
      promo,
      sitewidePromoContent,
      isRafGift,
    );

    initialize();
    setSkipMemSelectFlow(skipMemSelection);
    trackPageView('Landing');
  }

  useEffect(() => {
    // If making a server side call to get experiment UUID, we need to set the cookie here
    // This way that same uuid is used for all experiment calls made throughout the app
    if (experimentUUID) {
      setCookie(UUID_COOKIE_NAME, experimentUUID);
    }
    init();

    document.addEventListener('scroll', () => {
      whoopAnalyticsScrollTracker(trackAmplitudeEvent);
    });
    return () => {
      document.removeEventListener('scroll', () => {
        whoopAnalyticsScrollTracker(trackAmplitudeEvent);
      });
    };
  }, []);

  useEffect(() => {
    initializeStripePaymentOptions();
  }, [stripe]);

  return (
    <LandingPageContent
      isMobile={isMobile ?? false}
      promoContent={promoContent}
      skipMemSelection={skipMemSelection}
      skipMemFamilySize={skipMemFamilySize}
    />
  );
}
